import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "./useAuth";

export function useTagFollows(tagName?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Check if user follows a specific tag
  const { data: isFollowing, isLoading: checkingFollow } = useQuery({
    queryKey: ["tag-follow", tagName, user?.id],
    queryFn: async () => {
      if (!user || !tagName) return false;

      const { data, error } = await supabase
        .from("tag_follows")
        .select("id")
        .eq("user_id", user.id)
        .eq("tag_name", tagName)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!user && !!tagName,
  });

  // Get all tags the user follows
  const { data: followedTags = [], isLoading: loadingTags } = useQuery({
    queryKey: ["tag-follows", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from("tag_follows")
        .select("tag_name, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Follow a tag
  const followTag = useMutation({
    mutationFn: async (tag: string) => {
      if (!user) throw new Error("Must be logged in");

      const normalizedTag = tag.toLowerCase().trim();

      const { error } = await supabase
        .from("tag_follows")
        .insert({ user_id: user.id, tag_name: normalizedTag });

      if (error) throw error;
    },
    onSuccess: (_, tag) => {
      queryClient.invalidateQueries({ queryKey: ["tag-follow", tag, user?.id] });
      queryClient.invalidateQueries({ queryKey: ["tag-follows", user?.id] });
      toast.success(`Now following "${tag}"`);
    },
    onError: (error: any) => {
      console.error("Error following tag:", error);
      if (error.message?.includes("duplicate")) {
        toast.error("You're already following this tag");
      } else {
        toast.error("Failed to follow tag");
      }
    },
  });

  // Unfollow a tag
  const unfollowTag = useMutation({
    mutationFn: async (tag: string) => {
      if (!user) throw new Error("Must be logged in");

      const normalizedTag = tag.toLowerCase().trim();

      const { error } = await supabase
        .from("tag_follows")
        .delete()
        .eq("user_id", user.id)
        .eq("tag_name", normalizedTag);

      if (error) throw error;
    },
    onSuccess: (_, tag) => {
      queryClient.invalidateQueries({ queryKey: ["tag-follow", tag, user?.id] });
      queryClient.invalidateQueries({ queryKey: ["tag-follows", user?.id] });
      toast.success(`Unfollowed "${tag}"`);
    },
    onError: (error) => {
      console.error("Error unfollowing tag:", error);
      toast.error("Failed to unfollow tag");
    },
  });

  return {
    isFollowing,
    checkingFollow,
    followedTags,
    loadingTags,
    followTag: followTag.mutate,
    unfollowTag: unfollowTag.mutate,
    isFollowingTag: followTag.isPending,
    isUnfollowingTag: unfollowTag.isPending,
  };
}
